// SkipEnquiryCreatePage — /skip/enquiries/create.

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { CustomerSearch } from '../../bookings/components/CustomerSearch';
import { useCreateSkipEnquiry } from '../hooks/useSkipEnquiries';
import { useSkipSizes } from '../hooks/useSkipJobs';
import { useActivatedLegacySkipTypes } from '../hooks/useActivatedLegacySkipTypes';
import { useSkipJobTypes } from '../hooks/useSkipJobTypes';
import { DuplicateCustomerWarning } from '../../../components/ui/DuplicateCustomerWarning/DuplicateCustomerWarning';
import { useCheckCustomerDuplicates } from '../hooks/useSkipCustomers';
import {
  SitePicker,
  type SitePickerValue,
} from '../../../components/ui/SitePicker/SitePicker';
import type { CustomerSummary } from '../../../types/api';
import { extractErrorMessage } from '../../../utils/errorMessage';
import { StagingFileInput } from '../../../components/ui/StagingFileInput/StagingFileInput';
import { uploadEnquiryAttachmentsBackground } from '../../../api/skipAttachments';
import { useToast } from '../../../hooks/useToast';
import styles from './SkipEnquiryCreatePage.module.css';

const LEGACY_LABELS: Readonly<Record<string, string>> = {
  skip: 'Skip',
  bin: 'Bin',
  roro: 'Roro',
};

interface FormState {
  customer_uuid: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string;
  customer_phone2: string | null;
  customer_postcode: string;
  skip_type: 'skip' | 'bin' | 'roro' | 'other' | '';
  /** Bug 2 — enc_id of a custom (non-legacy) stock type, when one is
   *  selected. skip_type stays 'other' on the persisted row. */
  custom_stock_type_uuid: string | null;
  /** Encrypted enc_id of the chosen skip_stock_sizes row. The
   *  /reference/skip-sizes catalogue returns enc_ids; the form binds
   *  the string directly (Number(enc_id) is NaN). */
  skip_size_enc_id: string | null;
  job_type: string;
  /** enc_id of the selected skip_job_types row. The form binds the
   *  job-type dropdown to this; the create payload sends it as
   *  `skip_job_type_enc_id`. */
  skip_job_type_enc_id: string | null;
  preferred_delivery_date: string;
  follow_up_date: string;
  estimated_price: string;
  is_priority: boolean;
  priority_notes: string;
  notes: string;
  site_enc_id?: string | null;
  customer_address_line1?: string | null;
  customer_address_line2?: string | null;
  customer_address_city?: string | null;
  customer_county?: string | null;
}

const EMPTY_FORM: FormState = {
  customer_uuid: null,
  customer_name: '',
  customer_email: null,
  customer_phone: '',
  customer_phone2: null,
  customer_postcode: '',
  skip_type: '',
  custom_stock_type_uuid: null,
  skip_size_enc_id: null,
  job_type: '',
  skip_job_type_enc_id: null,
  preferred_delivery_date: '',
  follow_up_date: '',
  estimated_price: '',
  is_priority: false,
  priority_notes: '',
  notes: '',
  site_enc_id: null,
  customer_address_line1: null,
  customer_address_line2: null,
  customer_address_city: null,
  customer_county: null,
};

export function SkipEnquiryCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [newCustomerMode, setNewCustomerMode] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [duplicateMatches, setDuplicateMatches] = useState<CustomerSummary[]>([]);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [userChoseCreateAnyway, setUserChoseCreateAnyway] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const checkDuplicates = useCheckCustomerDuplicates();

  const createEnquiry = useCreateSkipEnquiry();
  const sizesQuery = useSkipSizes();
  const activatedLegacy = useActivatedLegacySkipTypes();
  const jobTypesQuery = useSkipJobTypes({ forSelection: true });
  const jobTypes = jobTypesQuery.data?.jobTypes ?? [];
  const selectedJobType = jobTypes.find((t) => t.enc_id === form.skip_job_type_enc_id) ?? null;
  const isImmediate = selectedJobType?.process_immediately === true;

  // Skip Type options on enquiries lead with "Not specified" since
  // enquiry data is captured incrementally and the type may be unknown
  // at the point of creation. Activated legacy types follow, then the
  // generic 'other' catch-all. Bug 2 — own-tenant custom stock types
  // (company_id IS NOT NULL) are emitted as `custom:${enc_id}` options
  // so they can be selected first-class instead of forcing the user to
  // pick "Other".
  const typeOptions = useMemo(
    () => [
      { value: '', label: '— Not specified —' },
      ...activatedLegacy.types.map((t) => ({ value: t as string, label: LEGACY_LABELS[t] ?? t })),
      ...activatedLegacy.customTypes.map((c) => ({
        value: `custom:${c.uuid}`,
        label: c.name,
      })),
      { value: 'other', label: 'Other' },
    ],
    [activatedLegacy.types, activatedLegacy.customTypes],
  );

  function handleCustomerSelect(c: CustomerSummary) {
    setForm((prev) => ({
      ...prev,
      customer_uuid: c.uuid,
      customer_name: c.name,
      customer_email: c.email ?? null,
      customer_phone: c.phone,
      customer_phone2: c.phone2 ?? null,
      customer_postcode: c.postcode ?? '',
    }));
    setNewCustomerMode(false);
  }

  async function handleCustomerBlur(): Promise<void> {
    if (!newCustomerMode || !form.customer_name) return;
    try {
      const result = await checkDuplicates.mutateAsync({
        name:     form.customer_name,
        phone:    form.customer_phone || null,
        postcode: form.customer_postcode || null,
      });
      setDuplicateMatches(result.matches);
    } catch {
      // non-fatal — duplicate check failure should not block submission
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.customer_name || !form.customer_phone || !form.customer_postcode) {
      setError('Customer name, phone and postcode are required.');
      return;
    }
    if (newCustomerMode && duplicateMatches.length > 0 && !userChoseCreateAnyway) {
      setShowDuplicateConfirm(true);
      return;
    }
    const body: Record<string, unknown> = {
      customer_name: form.customer_name,
      customer_phone: form.customer_phone,
      customer_postcode: form.customer_postcode,
      customer_email: form.customer_email,
      customer_phone2: form.customer_phone2,
      is_priority: form.is_priority,
      notes: form.notes || null,
    };
    if (form.customer_uuid) body['customer_uuid'] = form.customer_uuid;
    if (form.skip_type) body['skip_type'] = form.skip_type;
    if (form.skip_size_enc_id !== null) body['skip_size_enc_id'] = form.skip_size_enc_id;
    if (form.job_type) body['job_type'] = form.job_type;
    if (form.skip_job_type_enc_id) {
      body['skip_job_type_enc_id'] = form.skip_job_type_enc_id;
    }
    // process_immediately enquiries skip preferred date — they are
    // expected to be converted instantly on save.
    if (form.preferred_delivery_date && !isImmediate) {
      body['preferred_delivery_date'] = form.preferred_delivery_date;
    }
    if (form.follow_up_date) body['follow_up_date'] = form.follow_up_date;
    if (form.estimated_price) {
      body['estimated_price'] = Number(form.estimated_price);
    }
    if (form.is_priority && form.priority_notes) {
      body['priority_notes'] = form.priority_notes;
    }
    // Driver is captured at conversion time on ProcessEnquiryPage,
    // not here. Enquiries don't carry a driver_id column.
    // Phase 6D — site picker
    const fSite = form.site_enc_id ?? null;
    const fLine1 = form.customer_address_line1 ?? null;
    const fLine2 = form.customer_address_line2 ?? null;
    const fCity = form.customer_address_city ?? null;
    const fCounty = form.customer_county ?? null;
    if (fSite) {
      body['site_enc_id'] = fSite;
    } else if (fLine1) {
      body['site_enc_id'] = null;
      body['customer_address_line1'] = fLine1;
      if (fLine2) body['customer_address_line2'] = fLine2;
      if (fCity) body['customer_city'] = fCity;
    }
    if (fCounty) body['customer_county'] = fCounty;
    // For new-customer mode, ensure customer profile address is always sent
    // even when a saved site is also selected.
    if (newCustomerMode) {
      if (fLine1 && !body['customer_address_line1']) body['customer_address_line1'] = fLine1;
      if (fLine2 && !body['customer_address_line2']) body['customer_address_line2'] = fLine2;
      if (fCity  && !body['customer_city'])          body['customer_city']          = fCity;
      if (fCounty && !body['customer_county'])        body['customer_county']        = fCounty;
    }

    createEnquiry.mutate(body, {
      onSuccess: (data) => {
        const encId = data.enquiry.enc_id;
        // Single-flow attachments: fire the upload in the background and
        // navigate immediately — the detail page refetches and shows them.
        if (stagedFiles.length > 0) {
          void uploadEnquiryAttachmentsBackground(encId, stagedFiles, () =>
            toast.error(
              'Enquiry created, but attachments failed to upload. Add them from the enquiry page.',
            ),
          );
        }
        navigate(`/skip/enquiries/${encodeURIComponent(encId)}`);
      },
      onError: (err) => setError(extractErrorMessage(err)),
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="New Enquiry"
        subtitle="Capture a new customer skip-hire enquiry"
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/skip/enquiries')}
          >
            Back to Enquiries
          </Button>
        }
      />

      <form ref={formRef} onSubmit={handleSubmit} className={styles.form}>
        <Card>
          <h2 className={styles.sectionHeading}>Customer Details</h2>
          <CustomerSearch
            newCustomerMode={newCustomerMode}
            onSelect={handleCustomerSelect}
            onToggleNewCustomer={(nm) => {
              setNewCustomerMode(nm);
              if (nm) setForm(() => ({ ...EMPTY_FORM }));
              setDuplicateMatches([]);
              setUserChoseCreateAnyway(false);
              setShowDuplicateConfirm(false);
            }}
          />
          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Name</span>
              <input
                required
                value={form.customer_name ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, customer_name: e.target.value }))}
                onBlur={() => { void handleCustomerBlur(); }}
              />
            </label>
            <label className={styles.field}>
              <span>Phone</span>
              <input
                required
                value={form.customer_phone ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, customer_phone: e.target.value }))}
                onBlur={() => { void handleCustomerBlur(); }}
              />
            </label>
            <label className={styles.field}>
              <span>Email</span>
              <input
                type="email"
                value={form.customer_email ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, customer_email: e.target.value || null }))
                }
              />
            </label>
            <label className={styles.field}>
              <span>Postcode</span>
              <input
                required
                value={form.customer_postcode ?? ''}
                onChange={(e) => setForm((prev) => ({ ...prev, customer_postcode: e.target.value }))}
              />
            </label>
            {newCustomerMode && (
              <>
                <label className={styles.field}>
                  <span>Address</span>
                  <input
                    value={form.customer_address_line1 ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, customer_address_line1: e.target.value || null }))
                    }
                    placeholder="Street address"
                  />
                </label>
                <label className={styles.field}>
                  <span>City</span>
                  <input
                    value={form.customer_address_city ?? ''}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, customer_address_city: e.target.value || null }))
                    }
                    placeholder="City"
                  />
                </label>
              </>
            )}
            {newCustomerMode && (
              <label className={styles.field}>
                <span>County</span>
                <input
                  value={form.customer_county ?? ''}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, customer_county: e.target.value || null }))
                  }
                />
              </label>
            )}
          </div>
        </Card>

        {newCustomerMode && duplicateMatches.length > 0 && (
          <DuplicateCustomerWarning
            matches={duplicateMatches}
            showConfirm={showDuplicateConfirm}
            onUseExisting={(c) => {
              handleCustomerSelect(c);
              setDuplicateMatches([]);
              setUserChoseCreateAnyway(false);
            }}
            onCreateAnyway={() => {
              setUserChoseCreateAnyway(true);
              setDuplicateMatches([]);
            }}
            onDismiss={() => setDuplicateMatches([])}
            onConfirmCreate={() => {
              setUserChoseCreateAnyway(true);
              setShowDuplicateConfirm(false);
              formRef.current?.requestSubmit();
            }}
            onCancelConfirm={() => setShowDuplicateConfirm(false)}
          />
        )}

        <Card>
          <h2 className={styles.sectionHeading}>Job Site</h2>
          <SitePicker
            customerEncId={form.customer_uuid}
            onChange={(value: SitePickerValue) => {
              if (value.type === 'site') {
                setForm((prev) => ({
                  ...prev,
                  site_enc_id: value.site_enc_id,
                  customer_address_line1: null,
                  customer_address_line2: null,
                  customer_address_city: null,
                }));
              } else if (value.type === 'one_off') {
                setForm((prev) => ({
                  ...prev,
                  site_enc_id: null,
                  customer_address_line1: value.line1,
                  customer_address_line2: value.line2,
                  customer_address_city: value.city,
                }));
              }
            }}
          />
        </Card>

        <Card>
          <h2 className={styles.sectionHeading}>Stock / Job Details</h2>
          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Stock Type</span>
              <select
                value={
                  form.custom_stock_type_uuid !== null
                    ? `custom:${form.custom_stock_type_uuid}`
                    : form.skip_type
                }
                onChange={(e) => {
                  // Bug 3: only reset the size when the type ACTUALLY
                  // changes. Re-selecting the current type used to nuke
                  // the chosen size unexpectedly.
                  const v = e.target.value;
                  if (v.startsWith('custom:')) {
                    const uuid = v.slice('custom:'.length);
                    setForm((prev) => {
                      if (prev.custom_stock_type_uuid === uuid) return prev;
                      return {
                        ...prev,
                        skip_type: 'other',
                        custom_stock_type_uuid: uuid,
                        skip_size_enc_id: null,
                      };
                    });
                  } else {
                    const next = v as FormState['skip_type'];
                    setForm((prev) => {
                      if (next === prev.skip_type && prev.custom_stock_type_uuid === null) return prev;
                      return {
                        ...prev,
                        skip_type: next,
                        custom_stock_type_uuid: null,
                        skip_size_enc_id: null,
                      };
                    });
                  }
                }}
              >
                {typeOptions.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Stock Size</span>
              <select
                value={form.skip_size_enc_id ?? ''}
                onChange={(e) => {
                  const nextSize = e.target.value === '' ? null : e.target.value;
                  setForm((prev) => ({
                    ...prev,
                    skip_size_enc_id: nextSize,
                  }));
                }}
                disabled={
                  form.custom_stock_type_uuid === null &&
                  (form.skip_type === '' || form.skip_type === 'other')
                }
              >
                <option value="">
                  {form.custom_stock_type_uuid === null &&
                  (form.skip_type === '' || form.skip_type === 'other')
                    ? '— Select type first —'
                    : '— Select size —'}
                </option>
                {(sizesQuery.data?.skipSizes ?? [])
                  // Empty (Not specified) and 'other' have no scoped
                  // size set — show nothing until a real type is picked.
                  // Bug 2 — when a custom is chosen, filter by parent
                  // stock type's enc_id rather than the legacy enum.
                  .filter((s) =>
                    form.custom_stock_type_uuid !== null
                      ? s.stock_type_uuid === form.custom_stock_type_uuid
                      : form.skip_type !== '' &&
                        form.skip_type !== 'other' &&
                        s.skip_type === form.skip_type,
                  )
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Job Type</span>
              <select
                value={form.skip_job_type_enc_id ?? ''}
                onChange={(e) => {
                  const encIdValue = e.target.value || null;
                  const found = jobTypes.find((t) => t.enc_id === encIdValue);
                  setForm((prev) => ({
                    ...prev,
                    skip_job_type_enc_id: encIdValue,
                    job_type: found?.key ?? '',
                  }));
                }}
              >
                <option value="">— Not specified —</option>
                {jobTypes.map((t) => (
                  <option key={t.enc_id} value={t.enc_id}>
                    {t.name}
                    {t.process_immediately ? ' (Immediate)' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Estimated Price (£)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.estimated_price}
                onChange={(e) => setForm((prev) => ({ ...prev, estimated_price: e.target.value }))}
              />
            </label>
            <label className={styles.field}>
              <span>Preferred Delivery</span>
              <input
                type="date"
                value={form.preferred_delivery_date}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, preferred_delivery_date: e.target.value }))
                }
              />
            </label>
            <label className={styles.field}>
              <span>Follow-up Date</span>
              <input
                type="date"
                value={form.follow_up_date}
                onChange={(e) => setForm((prev) => ({ ...prev, follow_up_date: e.target.value }))}
              />
            </label>
          </div>
        </Card>

        <Card>
          <h2 className={styles.sectionHeading}>Notes &amp; Priority</h2>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={form.is_priority}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, is_priority: e.target.checked }))
              }
            />
            <span>Mark as priority enquiry</span>
          </label>
          {form.is_priority ? (
            <label className={styles.field}>
              <span>Priority notes</span>
              <textarea
                rows={2}
                maxLength={2000}
                value={form.priority_notes}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, priority_notes: e.target.value }))
                }
                placeholder="Why is this priority?"
              />
            </label>
          ) : null}
          <label className={styles.field}>
            <span>Notes</span>
            <textarea
              rows={4}
              maxLength={5000}
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              placeholder="Anything we should know about this enquiry…"
            />
          </label>
        </Card>

        <Card>
          <h2 className={styles.sectionHeading}>Attachments</h2>
          <StagingFileInput
            files={stagedFiles}
            onChange={setStagedFiles}
            maxFiles={20}
            disabled={createEnquiry.isPending}
          />
        </Card>

        {error ? <p className={styles.error}>{error}</p> : null}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/skip/enquiries')}
            disabled={createEnquiry.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={createEnquiry.isPending}>
            {createEnquiry.isPending ? 'Creating…' : 'Create Enquiry'}
          </Button>
        </div>
      </form>
    </div>
  );
}
